import type { CallingProviderOverview } from '@/api/endpoints/platform';

/**
 * What a calling-provider setting does to whom (AdminCallingProvider, W6).
 * Mirrors server/src/application/calling/callingProvider.ts: `asterisk`
 * with an empty pilot list takes every caller; with pilots, only them.
 */
export type Member = CallingProviderOverview['members'][number];

export interface ProviderImpact {
  provider: 'twilio' | 'asterisk';
  /** Distinct people (by e-mail) whose calls go through Asterisk / Twilio. */
  asterisk: Member[];
  twilio: Member[];
  /** Organisations with at least one person on Asterisk. */
  asteriskOrganisations: number;
  organisations: number;
  /** Pilot e-mails that match no active caller: they change nothing. */
  unknownPilots: string[];
}

function distinctByEmail(members: Member[]): Member[] {
  const seen = new Map<string, Member>();
  for (const m of members) if (!seen.has(m.email)) seen.set(m.email, m);
  return [...seen.values()];
}

export function providerImpact(provider: 'twilio' | 'asterisk', pilotEmails: string[], members: Member[]): ProviderImpact {
  const pilots = new Set(pilotEmails.map((e) => e.trim().toLowerCase()).filter(Boolean));
  const onAsterisk = (m: Member) => provider === 'asterisk' && (pilots.size === 0 || pilots.has(m.email.toLowerCase()));
  const asteriskRows = members.filter(onAsterisk);
  const known = new Set(members.map((m) => m.email.toLowerCase()));
  const asterisk = distinctByEmail(asteriskRows);
  const asteriskEmails = new Set(asterisk.map((m) => m.email));
  return {
    provider,
    asterisk,
    twilio: distinctByEmail(members).filter((m) => !asteriskEmails.has(m.email)),
    asteriskOrganisations: new Set(asteriskRows.map((m) => m.organisation_id)).size,
    organisations: new Set(members.map((m) => m.organisation_id)).size,
    unknownPilots: [...pilots].filter((e) => !known.has(e)),
  };
}

/** Did the change touch who goes where? (Credentials alone do not need a confirmation.) */
export function routingChanged(before: { provider: string; pilotEmails: string[] }, after: { provider: string; pilotEmails: string[] }): boolean {
  if (before.provider !== after.provider) return true;
  if (after.provider !== 'asterisk') return false;
  const a = [...new Set(before.pilotEmails.map((e) => e.toLowerCase()))].sort().join(',');
  const b = [...new Set(after.pilotEmails.map((e) => e.toLowerCase()))].sort().join(',');
  return a !== b;
}

/** Minutes saved by billing to the second, and the share of the started-minute bill (null with no minute). */
export function perSecondSaving(usage: { started_minute: number; per_second: number }): { minutes: number; share: number | null } {
  const minutes = Math.max(0, Math.round((usage.started_minute - usage.per_second) * 100) / 100);
  return { minutes, share: usage.started_minute > 0 ? minutes / usage.started_minute : null };
}
